import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Send, CheckCircle, XCircle, Building2, Truck,
    Calendar, DollarSign, Package, Printer
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Badge, ConfirmDialog, toast, Tabs } from '../../components/common';
import { purchaseOrderApi } from '../../services/api';
import './PurchaseOrderDetail.css';

export default function PurchaseOrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [actionType, setActionType] = useState('');

    const { data: po, isLoading } = useQuery({
        queryKey: ['purchaseOrder', id],
        queryFn: () => purchaseOrderApi.getById(id),
        select: (res) => res.data?.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ action }) => {
            switch (action) {
                case 'submit': return purchaseOrderApi.submit(id);
                case 'approve': return purchaseOrderApi.approve(id);
                case 'send': return purchaseOrderApi.send(id, {});
                case 'cancel': return purchaseOrderApi.cancel(id, { reason: 'Cancelled' });
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const msgs = { submit: 'submitted', approve: 'approved', send: 'sent', cancel: 'cancelled' };
            toast.success(`PO ${msgs[action]} successfully`);
            queryClient.invalidateQueries(['purchaseOrder', id]);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'pending_approval': 'warning',
            'approved': 'info',
            'sent': 'info',
            'acknowledged': 'success',
            'completed': 'success',
            'cancelled': 'danger'
        };
        return <Badge variant={config[status] || 'default'} size="lg">{status?.replace('_', ' ')}</Badge>;
    };

    if (isLoading) return <div className="loading-state">Loading...</div>;
    if (!po) return <div className="error-state">Purchase Order not found</div>;

    const canSubmit = po.status === 'draft';
    const canApprove = po.status === 'pending_approval';
    const canSend = po.status === 'approved';
    const canCancel = ['draft', 'pending_approval'].includes(po.status);

    const lines = po.lines || po.items || [];

    const tabs = [
        {
            key: 'items',
            label: `Items (${lines.length})`,
            content: (
                <div className="po-items">
                    <table>
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Description</th>
                                <th>Qty</th>
                                <th>Unit Price</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <div className="item-info">
                                            <span className="item-code">{item.itemCode || item.catalogItem?.itemCode}</span>
                                            <span className="item-name">{item.itemName || item.catalogItem?.name}</span>
                                        </div>
                                    </td>
                                    <td>{item.specifications || item.description || '-'}</td>
                                    <td>{item.quantity} {item.uomCode || item.catalogItem?.unitOfMeasure?.code || 'PCS'}</td>
                                    <td>{formatCurrency(item.unitPrice)}</td>
                                    <td>{formatCurrency(item.totalPrice || (item.quantity * item.unitPrice))}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="4"><strong>Total</strong></td>
                                <td><strong>{formatCurrency(po.totalAmount)}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )
        },
        {
            key: 'delivery',
            label: 'Delivery Info',
            content: (
                <div className="po-delivery">
                    <div className="detail-item">
                        <label>Shipping Address</label>
                        <p>{po.shippingAddress || '-'}</p>
                    </div>
                    <div className="detail-item">
                        <label>Payment Terms</label>
                        <p>{po.paymentTerms || '-'}</p>
                    </div>
                    <div className="detail-item">
                        <label>Notes</label>
                        <p>{po.notes || '-'}</p>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="po-detail-page">
            <PageHeader
                title={po.poNumber}
                breadcrumbs={[
                    { label: 'Purchase Orders', path: '/purchase-orders' },
                    { label: po.poNumber }
                ]}
                actions={
                    <div className="po-actions">
                        <Button variant="secondary" icon={Printer} onClick={() => window.print()}>
                            Print
                        </Button>
                        {canSubmit && (
                            <Button icon={Send} onClick={() => setActionType('submit')}>
                                Submit
                            </Button>
                        )}
                        {canApprove && (
                            <Button icon={CheckCircle} onClick={() => setActionType('approve')}>
                                Approve
                            </Button>
                        )}
                        {canSend && (
                            <Button icon={Send} onClick={() => setActionType('send')}>
                                Send to Vendor
                            </Button>
                        )}
                        {canCancel && (
                            <Button variant="danger" icon={XCircle} onClick={() => setActionType('cancel')}>
                                Cancel
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="po-detail-content">
                <Card>
                    <CardBody>
                        <div className="po-header">
                            <div>
                                <h2>{po.poNumber}</h2>
                            </div>
                            {getStatusBadge(po.status)}
                        </div>

                        <div className="po-meta-grid">
                            <div className="meta-item">
                                <Building2 size={16} />
                                <span className="meta-label">Vendor</span>
                                <span className="meta-value">{po.vendor?.name}</span>
                            </div>
                            <div className="meta-item">
                                <DollarSign size={16} />
                                <span className="meta-label">Total</span>
                                <span className="meta-value">{formatCurrency(po.totalAmount)}</span>
                            </div>
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span className="meta-label">Created</span>
                                <span className="meta-value">{formatDate(po.createdAt)}</span>
                            </div>
                            <div className="meta-item">
                                <Truck size={16} />
                                <span className="meta-label">Delivery Date</span>
                                <span className="meta-value">{formatDate(po.deliveryDate)}</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <Tabs tabs={tabs} defaultTab="items" />
                </Card>
            </div>

            <ConfirmDialog
                isOpen={!!actionType}
                onClose={() => setActionType('')}
                onConfirm={() => actionMutation.mutate({ action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} PO`}
                message={`Are you sure you want to ${actionType} this purchase order?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={actionType === 'cancel' ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
